import { useState } from 'react';
import { useLoanApplication } from '../../../contexts/LoanApplicationContext';

export default function Step4ReviewSubmit() {
  const { applicationData, setCurrentStep } = useLoanApplication();
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptAccuracy, setAcceptAccuracy] = useState(false);
  const [acceptCreditCheck, setAcceptCreditCheck] = useState(false);
  const [acceptAMLKYC, setAcceptAMLKYC] = useState(false);
  const [digitalSignature, setDigitalSignature] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [applicationReference, setApplicationReference] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const generateReferenceNumber = () => {
    const timestamp = Date.now().toString(36).toUpperCase();
    const random = Math.random().toString(36).substring(2, 7).toUpperCase();
    return `NKL-${timestamp}-${random}`;
  };

  const handleEdit = (step: number) => {
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!acceptTerms) newErrors.terms = 'You must accept the terms and conditions';
    if (!acceptPrivacy) newErrors.privacy = 'You must accept the privacy policy';
    if (!acceptAccuracy) newErrors.accuracy = 'You must confirm information accuracy';
    if (!acceptCreditCheck) newErrors.creditCheck = 'You must consent to credit checks';
    if (!acceptAMLKYC) newErrors.amlkyc = 'You must consent to AML/KYC verification';
    if (!digitalSignature.trim()) {
      newErrors.signature = 'Digital signature is required';
    } else if (digitalSignature.trim().toLowerCase() !== 
               `${applicationData.personalInfo.firstName} ${applicationData.personalInfo.lastName}`.toLowerCase()) {
      newErrors.signature = 'Signature must match your full name';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      const firstError = document.querySelector('.border-red-500');
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsSubmitting(true);

    try {
      const refNumber = generateReferenceNumber();
      
      // Prepare form data
      const formData = new FormData();
      
      // Personal Information
      formData.append('firstName', applicationData.personalInfo.firstName);
      formData.append('lastName', applicationData.personalInfo.lastName);
      formData.append('dateOfBirth', applicationData.personalInfo.dateOfBirth);
      formData.append('gender', applicationData.personalInfo.gender);
      formData.append('maritalStatus', applicationData.personalInfo.maritalStatus);
      formData.append('nationalId', applicationData.personalInfo.nationalId);
      formData.append('physicalAddress', applicationData.personalInfo.physicalAddress);
      formData.append('city', applicationData.personalInfo.city);
      formData.append('province', applicationData.personalInfo.province);
      formData.append('phoneNumber', applicationData.personalInfo.phoneNumber);
      formData.append('email', applicationData.personalInfo.email);
      formData.append('employmentStatus', applicationData.personalInfo.employmentStatus);
      formData.append('monthlyIncome', applicationData.personalInfo.monthlyIncome);
      
      if (applicationData.personalInfo.employmentStatus === 'employed') {
        formData.append('employerName', applicationData.personalInfo.employerName);
        formData.append('employerAddress', applicationData.personalInfo.employerAddress);
        formData.append('jobTitle', applicationData.personalInfo.jobTitle);
      }
      
      if (applicationData.personalInfo.employmentStatus === 'self-employed' || 
          applicationData.personalInfo.employmentStatus === 'business-owner') {
        formData.append('businessName', applicationData.personalInfo.businessName);
        formData.append('businessType', applicationData.personalInfo.businessType);
        formData.append('businessRegistration', applicationData.personalInfo.businessRegistration);
      }

      // Loan Details
      formData.append('loanType', applicationData.loanDetails.loanType || '');
      formData.append('loanAmount', applicationData.loanDetails.loanAmount?.toString() || '');
      formData.append('loanTerm', applicationData.loanDetails.loanTerm?.toString() || '');
      formData.append('loanPurpose', applicationData.loanDetails.loanPurpose || '');
      
      if (applicationData.loanDetails.otherPurpose) {
        formData.append('otherPurpose', applicationData.loanDetails.otherPurpose);
      }
      
      if (applicationData.loanDetails.businessRegNumber) {
        formData.append('businessRegNumber', applicationData.loanDetails.businessRegNumber);
      }
      
      if (applicationData.loanDetails.yearsInOperation) {
        formData.append('yearsInOperation', applicationData.loanDetails.yearsInOperation);
      }
      
      if (applicationData.loanDetails.assetDescription) {
        formData.append('assetDescription', applicationData.loanDetails.assetDescription);
      }
      
      if (applicationData.loanDetails.assetValue) {
        formData.append('assetValue', applicationData.loanDetails.assetValue);
      }

      // Documents
      if (applicationData.documents) {
        Object.entries(applicationData.documents).forEach(([key, doc]) => {
          if (doc.file) {
            formData.append(key, doc.file);
          }
        });
      }

      // Declarations
      formData.append('acceptTerms', acceptTerms.toString());
      formData.append('acceptPrivacy', acceptPrivacy.toString());
      formData.append('acceptAccuracy', acceptAccuracy.toString());
      formData.append('acceptCreditCheck', acceptCreditCheck.toString());
      formData.append('acceptAMLKYC', acceptAMLKYC.toString());
      formData.append('digitalSignature', digitalSignature);
      formData.append('applicationReference', refNumber);
      formData.append('submissionDate', new Date().toISOString());

      // Submit to form endpoint
      const response = await fetch('https://readdy.ai/api/form/d62v5qdsi3r5mpi9undg', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        setApplicationReference(refNumber);
        setIsSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setErrors({ submit: 'Failed to submit application. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (isSubmitted) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-checkbox-circle-fill text-5xl text-green-600"></i>
          </div>

          <h2 className="text-3xl font-bold text-gray-900 mb-3">Application Submitted Successfully!</h2>
          <p className="text-gray-600 mb-8">
            Thank you for applying with Nakhalat Finance. Your application has been received and is being processed.
          </p>

          <div className="bg-teal-50 border-2 border-teal-200 rounded-lg p-6 mb-8">
            <p className="text-sm text-gray-700 mb-2">Your Application Reference Number</p>
            <p className="text-3xl font-bold text-teal-600 tracking-wider">{applicationReference}</p>
            <p className="text-sm text-gray-600 mt-2">Please save this reference number for tracking your application</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <i className="ri-information-line text-teal-600"></i>
              What Happens Next?
            </h3>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-teal-600">1</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Email Confirmation</h4>
                  <p className="text-sm text-gray-600">
                    You will receive a confirmation email at <strong>{applicationData.personalInfo.email}</strong> within the next few minutes.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-teal-600">2</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Document Verification</h4>
                  <p className="text-sm text-gray-600">
                    Our team will verify your documents and conduct KYC/AML checks. This typically takes 1-2 business days.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-teal-600">3</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Credit Assessment</h4>
                  <p className="text-sm text-gray-600">
                    We will conduct a credit assessment and evaluate your application based on RBZ lending guidelines.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="font-bold text-teal-600">4</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Decision & Disbursement</h4>
                  <p className="text-sm text-gray-600">
                    You will be notified of the decision within 3-5 business days. If approved, funds will be disbursed to your account.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8 text-left">
            <div className="flex gap-3">
              <i className="ri-time-line text-blue-600 text-xl flex-shrink-0"></i>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Processing Timeline</h4>
                <p className="text-sm text-gray-700">
                  Most applications are processed within <strong>3-5 business days</strong>. We may contact you if additional information is required.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8 text-left">
            <div className="flex gap-3">
              <i className="ri-phone-line text-amber-600 text-xl flex-shrink-0"></i>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Need Help?</h4>
                <p className="text-sm text-gray-700 mb-2">
                  If you have any questions about your application, please contact us:
                </p>
                <div className="space-y-1 text-sm">
                  <p className="text-gray-700">
                    <strong>Phone:</strong> +263 242 123 456
                  </p>
                  <p className="text-gray-700">
                    <strong>Email:</strong> applications@nakhalat.co.zw
                  </p>
                  <p className="text-gray-700">
                    <strong>Hours:</strong> Monday - Friday, 8:00 AM - 5:00 PM
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handlePrint}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap cursor-pointer"
            >
              <i className="ri-printer-line mr-2"></i>
              Print Summary
            </button>
            <a
              href="/"
              className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap text-center"
            >
              <i className="ri-home-line mr-2"></i>
              Return to Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  const getLoanTypeName = (type: string) => {
    const types: Record<string, string> = {
      'salary-based': 'Salary-Based Loan',
      'sme-working-capital': 'SME & Working Capital',
      'microenterprise': 'Microenterprise Loan',
      'asset-backed': 'Asset-Backed Loan'
    };
    return types[type] || type;
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Review & Submit Application</h2>
        <p className="text-gray-600">
          Please review your information carefully before submitting. You can edit any section if needed.
        </p>
      </div>

      {/* Personal Information Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <i className="ri-user-line text-teal-600"></i>
            Personal Information
          </h3>
          <button
            onClick={() => handleEdit(1)}
            className="text-sm text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap cursor-pointer"
          >
            <i className="ri-edit-line mr-1"></i>
            Edit
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-600 mb-1">Full Name</p>
            <p className="font-medium text-gray-900">
              {applicationData.personalInfo.firstName} {applicationData.personalInfo.lastName}
            </p>
          </div>
          <div>
            <p className="text-gray-600 mb-1">Date of Birth</p>
            <p className="font-medium text-gray-900">{applicationData.personalInfo.dateOfBirth}</p>
          </div>
          <div>
            <p className="text-gray-600 mb-1">Gender</p>
            <p className="font-medium text-gray-900 capitalize">{applicationData.personalInfo.gender}</p>
          </div>
          <div>
            <p className="text-gray-600 mb-1">Marital Status</p>
            <p className="font-medium text-gray-900 capitalize">{applicationData.personalInfo.maritalStatus}</p>
          </div>
          <div>
            <p className="text-gray-600 mb-1">National ID</p>
            <p className="font-medium text-gray-900">{applicationData.personalInfo.nationalId}</p>
          </div>
          <div>
            <p className="text-gray-600 mb-1">Phone Number</p>
            <p className="font-medium text-gray-900">{applicationData.personalInfo.phoneNumber}</p>
          </div>
          <div className="md:col-span-2">
            <p className="text-gray-600 mb-1">Email Address</p>
            <p className="font-medium text-gray-900">{applicationData.personalInfo.email}</p>
          </div>
          <div className="md:col-span-2">
            <p className="text-gray-600 mb-1">Physical Address</p>
            <p className="font-medium text-gray-900">
              {applicationData.personalInfo.physicalAddress}, {applicationData.personalInfo.city}, {applicationData.personalInfo.province}
            </p>
          </div>
          <div>
            <p className="text-gray-600 mb-1">Employment Status</p>
            <p className="font-medium text-gray-900 capitalize">
              {applicationData.personalInfo.employmentStatus.replace('-', ' ')}
            </p>
          </div>
          <div>
            <p className="text-gray-600 mb-1">Monthly Income</p>
            <p className="font-medium text-gray-900">${applicationData.personalInfo.monthlyIncome}</p>
          </div>
          
          {applicationData.personalInfo.employmentStatus === 'employed' && (
            <>
              <div>
                <p className="text-gray-600 mb-1">Employer Name</p>
                <p className="font-medium text-gray-900">{applicationData.personalInfo.employerName}</p>
              </div>
              <div>
                <p className="text-gray-600 mb-1">Job Title</p>
                <p className="font-medium text-gray-900">{applicationData.personalInfo.jobTitle}</p>
              </div>
            </>
          )}
          
          {(applicationData.personalInfo.employmentStatus === 'self-employed' || 
            applicationData.personalInfo.employmentStatus === 'business-owner') && (
            <>
              <div>
                <p className="text-gray-600 mb-1">Business Name</p>
                <p className="font-medium text-gray-900">{applicationData.personalInfo.businessName}</p>
              </div>
              <div>
                <p className="text-gray-600 mb-1">Business Type</p>
                <p className="font-medium text-gray-900">{applicationData.personalInfo.businessType}</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Loan Details Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <i className="ri-money-dollar-circle-line text-teal-600"></i>
            Loan Details
          </h3>
          <button
            onClick={() => handleEdit(2)}
            className="text-sm text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap cursor-pointer"
          >
            <i className="ri-edit-line mr-1"></i>
            Edit
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-gray-600 mb-1">Loan Type</p>
            <p className="font-medium text-gray-900">{getLoanTypeName(applicationData.loanDetails.loanType || '')}</p>
          </div>
          <div>
            <p className="text-gray-600 mb-1">Loan Amount</p>
            <p className="font-medium text-gray-900">${applicationData.loanDetails.loanAmount?.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-gray-600 mb-1">Loan Term</p>
            <p className="font-medium text-gray-900">{applicationData.loanDetails.loanTerm} months</p>
          </div>
          <div>
            <p className="text-gray-600 mb-1">Purpose</p>
            <p className="font-medium text-gray-900">
              {applicationData.loanDetails.loanPurpose === 'Other' 
                ? applicationData.loanDetails.otherPurpose 
                : applicationData.loanDetails.loanPurpose}
            </p>
          </div>

          {applicationData.loanDetails.businessRegNumber && (
            <>
              <div>
                <p className="text-gray-600 mb-1">Business Registration Number</p>
                <p className="font-medium text-gray-900">{applicationData.loanDetails.businessRegNumber}</p>
              </div>
              <div>
                <p className="text-gray-600 mb-1">Years in Operation</p>
                <p className="font-medium text-gray-900 capitalize">
                  {applicationData.loanDetails.yearsInOperation?.replace('-', ' ')}
                </p>
              </div>
            </>
          )}

          {applicationData.loanDetails.assetDescription && (
            <>
              <div className="md:col-span-2">
                <p className="text-gray-600 mb-1">Asset Description</p>
                <p className="font-medium text-gray-900">{applicationData.loanDetails.assetDescription}</p>
              </div>
              <div>
                <p className="text-gray-600 mb-1">Estimated Asset Value</p>
                <p className="font-medium text-gray-900">${applicationData.loanDetails.assetValue}</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Documents Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <i className="ri-file-list-line text-teal-600"></i>
            Uploaded Documents
          </h3>
          <button
            onClick={() => handleEdit(3)}
            className="text-sm text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap cursor-pointer"
          >
            <i className="ri-edit-line mr-1"></i>
            Edit
          </button>
        </div>

        <div className="space-y-3">
          {applicationData.documents && Object.entries(applicationData.documents).map(([key, doc]) => (
            <div key={key} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-file-text-line text-teal-600"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 text-sm truncate">{doc.file.name}</p>
                <p className="text-xs text-gray-600">
                  {(doc.file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <i className="ri-checkbox-circle-fill text-green-600 text-xl"></i>
            </div>
          ))}
        </div>
      </div>

      {/* Terms and Declarations */}
      <form data-readdy-form className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <i className="ri-file-shield-line text-teal-600"></i>
          Terms & Declarations
        </h3>

        <div className="space-y-4">
          {/* Terms and Conditions */}
          <div className={`p-4 rounded-lg border-2 ${errors.terms ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="acceptTerms"
                checked={acceptTerms}
                onChange={(e) => {
                  setAcceptTerms(e.target.checked);
                  if (e.target.checked && errors.terms) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.terms;
                      return newErrors;
                    });
                  }
                }}
                className="mt-1 w-5 h-5 text-teal-600 rounded focus:ring-2 focus:ring-teal-500 cursor-pointer"
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  I accept the{' '}
                  <a href="/terms" target="_blank" className="text-teal-600 hover:text-teal-700 underline">
                    Terms and Conditions
                  </a>
                  <span className="text-red-500 ml-1">*</span>
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  I have read and agree to the loan terms, interest rates, repayment schedule, and all conditions.
                </p>
              </div>
            </label>
            {errors.terms && <p className="text-sm text-red-600 mt-2 ml-8">{errors.terms}</p>}
          </div>

          {/* Privacy Policy */}
          <div className={`p-4 rounded-lg border-2 ${errors.privacy ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="acceptPrivacy"
                checked={acceptPrivacy}
                onChange={(e) => {
                  setAcceptPrivacy(e.target.checked);
                  if (e.target.checked && errors.privacy) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.privacy;
                      return newErrors;
                    });
                  }
                }}
                className="mt-1 w-5 h-5 text-teal-600 rounded focus:ring-2 focus:ring-teal-500 cursor-pointer"
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  I accept the{' '}
                  <a href="/privacy" target="_blank" className="text-teal-600 hover:text-teal-700 underline">
                    Privacy Policy
                  </a>
                  <span className="text-red-500 ml-1">*</span>
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  I consent to the collection, processing, and storage of my personal data as described in the privacy policy.
                </p>
              </div>
            </label>
            {errors.privacy && <p className="text-sm text-red-600 mt-2 ml-8">{errors.privacy}</p>}
          </div>

          {/* Information Accuracy */}
          <div className={`p-4 rounded-lg border-2 ${errors.accuracy ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="acceptAccuracy"
                checked={acceptAccuracy}
                onChange={(e) => {
                  setAcceptAccuracy(e.target.checked);
                  if (e.target.checked && errors.accuracy) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.accuracy;
                      return newErrors;
                    });
                  }
                }}
                className="mt-1 w-5 h-5 text-teal-600 rounded focus:ring-2 focus:ring-teal-500 cursor-pointer"
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  Declaration of Information Accuracy
                  <span className="text-red-500 ml-1">*</span>
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  I declare that all information provided in this application is true, accurate, and complete to the best of my knowledge. I understand that providing false information may result in application rejection or loan cancellation.
                </p>
              </div>
            </label>
            {errors.accuracy && <p className="text-sm text-red-600 mt-2 ml-8">{errors.accuracy}</p>}
          </div>

          {/* Credit Check Consent */}
          <div className={`p-4 rounded-lg border-2 ${errors.creditCheck ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="acceptCreditCheck"
                checked={acceptCreditCheck}
                onChange={(e) => {
                  setAcceptCreditCheck(e.target.checked);
                  if (e.target.checked && errors.creditCheck) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.creditCheck;
                      return newErrors;
                    });
                  }
                }}
                className="mt-1 w-5 h-5 text-teal-600 rounded focus:ring-2 focus:ring-teal-500 cursor-pointer"
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  Consent to Credit Checks
                  <span className="text-red-500 ml-1">*</span>
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  I authorize Nakhalat Finance to conduct credit checks and obtain credit reports from credit reference bureaus for the purpose of assessing my loan application.
                </p>
              </div>
            </label>
            {errors.creditCheck && <p className="text-sm text-red-600 mt-2 ml-8">{errors.creditCheck}</p>}
          </div>

          {/* AML/KYC Consent */}
          <div className={`p-4 rounded-lg border-2 ${errors.amlkyc ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="acceptAMLKYC"
                checked={acceptAMLKYC}
                onChange={(e) => {
                  setAcceptAMLKYC(e.target.checked);
                  if (e.target.checked && errors.amlkyc) {
                    setErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.amlkyc;
                      return newErrors;
                    });
                  }
                }}
                className="mt-1 w-5 h-5 text-teal-600 rounded focus:ring-2 focus:ring-teal-500 cursor-pointer"
              />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  AML/KYC Verification Consent
                  <span className="text-red-500 ml-1">*</span>
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  I consent to Anti-Money Laundering (AML) and Know Your Customer (KYC) verification procedures as required by RBZ regulations and Zimbabwe's financial laws.
                </p>
              </div>
            </label>
            {errors.amlkyc && <p className="text-sm text-red-600 mt-2 ml-8">{errors.amlkyc}</p>}
          </div>
        </div>

        {/* Digital Signature */}
        <div className="mt-6 pt-6 border-t border-gray-200">
          <label className="block text-sm font-semibold text-gray-900 mb-2">
            Digital Signature <span className="text-red-500">*</span>
          </label>
          <p className="text-sm text-gray-600 mb-3">
            Type your full name exactly as it appears in your National ID to sign this application digitally.
          </p>
          <input
            type="text"
            name="digitalSignature"
            value={digitalSignature}
            onChange={(e) => {
              setDigitalSignature(e.target.value);
              if (e.target.value && errors.signature) {
                setErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors.signature;
                  return newErrors;
                });
              }
            }}
            className={`w-full px-4 py-3 border-2 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all font-serif text-lg ${
              errors.signature ? 'border-red-500 bg-red-50' : 'border-gray-300'
            }`}
            placeholder={`${applicationData.personalInfo.firstName} ${applicationData.personalInfo.lastName}`}
          />
          {errors.signature && <p className="text-sm text-red-600 mt-2">{errors.signature}</p>}
          <p className="text-xs text-gray-500 mt-2">
            By typing your name, you are electronically signing this application and agreeing to all terms and declarations above.
          </p>
        </div>
      </form>

      {/* RBZ Compliance Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex gap-3">
          <i className="ri-shield-check-line text-blue-600 text-xl flex-shrink-0"></i>
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">RBZ Regulated & Compliant</h4>
            <p className="text-sm text-gray-700">
              Nakhalat Finance (Private) Limited is a licensed microfinance institution regulated by the Reserve Bank of Zimbabwe. 
              All loan products and processes comply with RBZ regulations, responsible lending principles, and Zimbabwe's financial laws.
            </p>
          </div>
        </div>
      </div>

      {/* Submit Error */}
      {errors.submit && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex gap-3">
            <i className="ri-error-warning-line text-red-600 text-xl flex-shrink-0"></i>
            <p className="text-sm text-red-700">{errors.submit}</p>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between pt-6 border-t border-gray-200">
        <button
          onClick={() => handleEdit(3)}
          disabled={isSubmitting}
          className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i className="ri-arrow-left-line mr-2"></i>
          Back
        </button>

        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="px-8 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isSubmitting ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Submitting Application...
            </>
          ) : (
            <>
              <i className="ri-send-plane-fill"></i>
              Submit Application
            </>
          )}
        </button>
      </div>
    </div>
  );
}